'use es6';

import { jsxs as _jsxs } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { Component } from 'react';
import PropTypes from 'prop-types';
import CustomWidgetPropTypes from 'DesignData/propTypes/customWidgets';
import { connect } from 'react-redux';
import text from 'I18n/utils/unescapedText';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { omit } from 'underscore';
import { byName as templateTypesByName } from 'ContentUtils/constants/TemplateTypes';
import { getHasDataCollectorQueryAccess } from 'DesignData/selectors/auth';
import * as customWidgetBuffersSelectors from 'DesignData/selectors/customWidgetBuffers';
import { getStylesheets } from 'DesignData/selectors/stylesheets';
import { getJavascripts } from 'DesignData/selectors/javascripts';
import { getGraphqlFilesOptions, getGraphqlFilesByPath } from 'DesignData/selectors/graphqlfiles';
import * as StylesheetsActions from 'DesignData/actions/Stylesheets';
import * as GraphQLActions from 'DesignData/actions/GraphqlFiles';
import * as JavascriptsActions from 'DesignData/actions/Javascripts';
import * as CustomWidgetBuffersActions from 'DesignData/actions/CustomWidgetBuffers';
import { getAbsolutePath, relative } from 'DesignData/lib/path';
import { i18nKey as notificationsI18nKey, showAppNotification } from 'DesignData/actions/AppNotifications';
import * as AssetActions from 'DesignData/actions/finder/Assets';
import { TEMPLATE } from 'DesignData/enums/DesignAssetTypes';
import { HOLLOW } from 'DesignData/enums/AssetSources';
import LinkedFileAssetTypes from 'DesignData/enums/LinkedFileAssetTypes';
import { createPath } from 'DesignData/helpers/routingHelper';
import UIRouterLink from 'ui-addon-react-router-dom/UIRouterLink';
import UITooltip from 'UIComponents/tooltip/UITooltip';
import Small from 'UIComponents/elements/Small';
import UICopyButton from 'UIComponents/button/UICopyButton';
import UIFlex from 'UIComponents/layout/UIFlex';
import ToolFormLabel from 'DesignComponents/tool/ToolFormLabel';
import SortableList from 'DesignEditorsUI/components/shared/inputs/SortableList';
import AddAssetLink from 'DesignEditorsUI/components/customWidgetEditor/sidebar/main/AddAssetLink';
import AddTemplateDropdown from 'DesignEditorsUI/components/shared/inputs/AddTemplateDropdown';
import UIIcon from 'UIComponents/icon/UIIcon';
import UILink from 'UIComponents/link/UILink';
import UISelect from 'UIComponents/input/UISelect';

function makeMapStateToProps() {
  const getAssets = customWidgetBuffersSelectors.makeGetAssets();
  const getModulePath = customWidgetBuffersSelectors.makeGetModulePath();
  const getAvailableStylesheets = customWidgetBuffersSelectors.makeGetAvailableStylesheets();
  const getAvailableJavascripts = customWidgetBuffersSelectors.makeGetAvailableJavascripts();
  const getIsEmailOrAnyType = customWidgetBuffersSelectors.makeGetIsEmailOrAnyType();
  const getAbsoluteDataQueryPath = customWidgetBuffersSelectors.makeGetAbsoluteDataQueryPath();
  return function mapStateToProps(state, props) {
    return {
      assets: getAssets(state, props),
      availableJavascripts: getAvailableJavascripts(state, props),
      availableStylesheets: getAvailableStylesheets(state, props),
      availableGraphqlFiles: getGraphqlFilesOptions(state, props),
      absoluteDataQueryPath: getAbsoluteDataQueryPath(state, props),
      graphqlFilesByPath: getGraphqlFilesByPath(state),
      hasDataCollectorAccess: getHasDataCollectorQueryAccess(state, props),
      modulePath: getModulePath(state, props),
      isEmailOrAnyType: getIsEmailOrAnyType(state, props),
      javascripts: getJavascripts(state, props),
      stylesheets: getStylesheets(state, props)
    };
  };
}

const mapDispatchToProps = {
  fetchStylesheets: StylesheetsActions.fetch,
  fetchJavascripts: JavascriptsActions.fetch,
  fetchGraphqlFiles: GraphQLActions.fetch,
  addAsset: CustomWidgetBuffersActions.addAsset,
  showAppNotification,
  openFinderRecentlyDeleted: AssetActions.openRecentlyDeleted
};

const i18nKey = key => `DesignEditorsUI.customWidgetEditor.sidebar.main.linkedFiles.${key}`;

const errorI18nKey = key => notificationsI18nKey(`linkedFiles.danger.${key}`);

function getAssetFromFile({
  url
}) {
  const urlParts = url.split('/');
  const name = urlParts[urlParts.length - 1];
  return {
    name,
    url
  };
}

function getExtensionFromFile({
  extension = ''
}) {
  return extension.toLowerCase();
}

class LinkedFiles extends Component {
  constructor(...args) {
    super(...args);

    this.onAssetsChange = (assetsType, assets) => {
      assets = assets.map(_asset => omit(_asset, ['id', 'label', 'tooltip']));
      this.props.updateCustomWidget({
        [assetsType]: assets
      });
    };

    this.onUpdateLinkedDataQuery = ({
      target: {
        value
      }
    }) => {
      const {
        modulePath
      } = this.props;

      if (value) {
        const relativePath = relative(modulePath, value);
        this.props.updateCustomWidget({
          dataQueryAbsolutePath: value,
          dataQueryPath: relativePath
        });
      } else {
        this.props.updateCustomWidget({
          dataQueryAbsolutePath: null,
          dataQueryPath: null
        });
      }
    };

    this.onAddAsset = (assetsType, asset) => {
      const {
        assets,
        customWidgetId,
        addAsset
      } = this.props;
      const containsAsset = Object.keys(assets).some(_assetsType => {
        return !!assets[_assetsType].find(_asset => _asset.name === asset.name);
      });

      if (containsAsset) {
        this.showErrorNotification('conflict', {
          fileName: asset.name
        });
      } else {
        addAsset(customWidgetId, assetsType, asset);
      }
    };

    this.onOtherFileSelect = fileData => {
      const asset = getAssetFromFile(fileData);
      const extension = getExtensionFromFile(fileData);

      if (extension === 'css' || extension === 'js') {
        this.showErrorNotification('cssJsInOtherAssets');
      } else {
        this.onAddAsset('otherAssets', asset);
      }
    };

    this.onCssSelect = id => {
      const stylesheet = this.props.stylesheets.byId[id];
      const asset = {
        name: stylesheet.label,
        resource_id: id,
        autoload: true
      };
      this.onAddAsset('cssAssets', asset);
    };

    this.onCssFileSelect = fileData => {
      const asset = Object.assign({}, getAssetFromFile(fileData), {
        autoload: true
      });

      if (getExtensionFromFile(fileData) !== 'css') {
        this.showErrorNotification('notCssInCssAssets');
      } else {
        this.onAddAsset('cssAssets', asset);
      }
    };

    this.onJsSelect = id => {
      const javascript = this.props.javascripts.byId[id];
      const asset = {
        name: javascript.label,
        resource_id: id,
        autoload: true
      };
      this.onAddAsset('jsAssets', asset);
    };

    this.onJsFileSelect = fileData => {
      const asset = Object.assign({}, getAssetFromFile(fileData), {
        autoload: true
      });

      if (getExtensionFromFile(fileData) !== 'js') {
        this.showErrorNotification('notJsInJsAssets');
      } else {
        this.onAddAsset('jsAssets', asset);
      }
    };

    this.showErrorNotification = (key, options = {}) => {
      this.props.showAppNotification({
        notificationKey: 'linkedFiles',
        type: 'danger',
        titleText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: errorI18nKey(`${key}.titleText`)
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: errorI18nKey(`${key}.message`),
          options: options
        })
      });
    };

    this.renderItemCopyAction = ({
      item
    }) => {
      const copySnippet = `{{ module_asset_url("${item.name}") }}`;
      return /*#__PURE__*/_jsx(UICopyButton, {
        className: "asset-item-action",
        hint: text(i18nKey('otherAssets.copyFileLinkHint')),
        use: "link",
        value: copySnippet,
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('otherAssets.copyFileLink')
          })
        })
      });
    };

    this.renderItemEditAction = ({
      item
    }) => {
      const assetPath = this.getAssetPath(item);

      const editLink = /*#__PURE__*/_jsx(UIRouterLink, {
        className: "asset-item-action",
        disabled: !assetPath,
        to: assetPath,
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey('editAssetLink.label')
          })
        })
      });

      return assetPath ? editLink : /*#__PURE__*/_jsx(UITooltip, {
        placement: "top left",
        title: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
          message: i18nKey('editAssetLink.disabledTooltip')
        }),
        children: editLink
      });
    };

    this.renderItemRestoreAction = () => {
      return /*#__PURE__*/_jsx(UITooltip, {
        placement: "top left",
        title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
          message: i18nKey('restoreAssetLink.tooltip_jsx'),
          options: {
            onClick: this.props.openFinderRecentlyDeleted
          },
          elements: {
            Link: UILink
          }
        }),
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(UIIcon, {
            name: "warning",
            size: 18
          })
        })
      });
    };

    this.renderItemAction = ({
      item
    }) => {
      const {
        deleted
      } = item;
      return deleted ? this.renderItemRestoreAction({
        item
      }) : this.renderItemEditAction({
        item
      });
    };

    this.renderAssetsTypeLabel = (assetsType, addButton) => {
      return /*#__PURE__*/_jsxs(UIFlex, {
        align: "baseline",
        justify: "between",
        children: [/*#__PURE__*/_jsx(ToolFormLabel, {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`${assetsType}.label`)
          })
        }), addButton]
      });
    };
  }

  componentDidMount() {
    const {
      hasDataCollectorAccess
    } = this.props;
    this.props.fetchStylesheets();
    this.props.fetchJavascripts();

    if (hasDataCollectorAccess) {
      this.props.fetchGraphqlFiles();
    }
  }

  getSortableListItemsForAssets(assets, allAssets) {
    return assets.map(_asset => {
      const {
        name,
        url,
        resource_id: resourceId,
        path
      } = _asset;
      const assetIsDeleted = resourceId && Object.keys(allAssets.byId).length && !allAssets.byId[resourceId];
      return Object.assign({
        id: url || resourceId || path,
        deleted: assetIsDeleted,
        label: name,
        tooltip: name,
        itemClassName: assetIsDeleted ? 'danger' : ''
      }, _asset);
    });
  }

  getAssetPath(item) {
    const {
      resource_id: resourceId,
      path
    } = item;

    if (resourceId) {
      return createPath(TEMPLATE, resourceId);
    } else if (path) {
      const {
        modulePath
      } = this.props;
      return createPath(TEMPLATE, getAbsolutePath(path, modulePath), {
        assetSource: HOLLOW
      });
    }

    return '';
  }

  renderAddAssetLink() {
    if (this.props.isReadOnly) {
      return null;
    }

    return /*#__PURE__*/_jsx(AddAssetLink, {
      onFileSelect: this.onOtherFileSelect
    });
  }

  renderAddStylesheetDropdown() {
    if (this.props.isReadOnly) {
      return null;
    }

    return /*#__PURE__*/_jsx(AddTemplateDropdown, {
      templates: this.props.availableStylesheets,
      onTemplateSelect: this.onCssSelect,
      onFileSelect: this.onCssFileSelect,
      templateType: templateTypesByName.css,
      enableFileManager: true
    });
  }

  renderAddJavascriptDropdown() {
    if (this.props.isReadOnly) {
      return null;
    }

    return /*#__PURE__*/_jsx(AddTemplateDropdown, {
      templates: this.props.availableJavascripts,
      onTemplateSelect: this.onJsSelect,
      onFileSelect: this.onJsFileSelect,
      templateType: templateTypesByName.js,
      enableFileManager: true
    });
  }

  renderLinkedStylesheets() {
    const {
      isEmailOrAnyType,
      isReadOnly
    } = this.props;

    if (isEmailOrAnyType) {
      return null;
    }

    return /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-4",
      children: [this.renderAssetsTypeLabel(LinkedFileAssetTypes.CSS_ASSETS, this.renderAddStylesheetDropdown()), /*#__PURE__*/_jsx(SortableList, {
        actionsComponent: this.renderItemAction,
        emptyText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('cssAssets.emptyText')
        }),
        isReadOnly: isReadOnly,
        itemClassName: "asset-item",
        items: this.getSortableListItemsForAssets(this.props.assets.cssAssets, this.props.stylesheets),
        onChange: (...partialArgs) => this.onAssetsChange(LinkedFileAssetTypes.CSS_ASSETS, ...partialArgs)
      })]
    });
  }

  renderLinkedJavascripts() {
    const {
      isEmailOrAnyType,
      isReadOnly
    } = this.props;

    if (isEmailOrAnyType) {
      return null;
    }

    return /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-4",
      children: [this.renderAssetsTypeLabel(LinkedFileAssetTypes.JS_ASSETS, this.renderAddJavascriptDropdown()), /*#__PURE__*/_jsx(SortableList, {
        actionsComponent: this.renderItemAction,
        emptyText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('jsAssets.emptyText')
        }),
        isReadOnly: isReadOnly,
        itemClassName: "asset-item",
        items: this.getSortableListItemsForAssets(this.props.assets.jsAssets, this.props.javascripts),
        onChange: (...partialArgs) => this.onAssetsChange(LinkedFileAssetTypes.JS_ASSETS, ...partialArgs)
      })]
    });
  }

  renderLinkedGraphql() {
    const {
      absoluteDataQueryPath,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      children: [this.renderAssetsTypeLabel('graphqlAssets'), /*#__PURE__*/_jsx(UISelect, {
        placeholder: text(i18nKey('graphqlAssets.emptyText')),
        defaultValue: absoluteDataQueryPath,
        clearable: true,
        disabled: isReadOnly,
        onChange: this.onUpdateLinkedDataQuery,
        options: this.props.availableGraphqlFiles
      })]
    });
  }

  renderOtherAssets() {
    const {
      isEmailOrAnyType,
      isReadOnly
    } = this.props;
    return /*#__PURE__*/_jsxs("div", {
      className: "m-bottom-4",
      children: [this.renderAssetsTypeLabel(isEmailOrAnyType ? 'assets' : LinkedFileAssetTypes.OTHER_ASSETS, this.renderAddAssetLink()), /*#__PURE__*/_jsx(SortableList, {
        actionsComponent: this.renderItemCopyAction,
        emptyText: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey('otherAssets.emptyText')
        }),
        isReadOnly: isReadOnly,
        itemClassName: "asset-item",
        items: this.getSortableListItemsForAssets(this.props.assets.otherAssets),
        onChange: (...partialArgs) => this.onAssetsChange(LinkedFileAssetTypes.OTHER_ASSETS, ...partialArgs)
      })]
    });
  }

  render() {
    return /*#__PURE__*/_jsxs("div", {
      className: "custom-widget-linked-files",
      children: [this.renderLinkedStylesheets(), this.renderLinkedJavascripts(), this.renderOtherAssets(), this.renderLinkedGraphql()]
    });
  }

}

LinkedFiles.propTypes = {
  customWidgetId: CustomWidgetPropTypes.customWidgetId.isRequired,
  absoluteDataQueryPath: PropTypes.string,
  assets: PropTypes.shape({
    otherAssets: PropTypes.arrayOf(PropTypes.object).isRequired,
    cssAssets: PropTypes.arrayOf(PropTypes.object).isRequired,
    jsAssets: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  availableGraphqlFiles: PropTypes.arrayOf(PropTypes.object),
  hasDataCollectorAccess: PropTypes.bool.isRequired,
  isEmailOrAnyType: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
  modulePath: PropTypes.string.isRequired,
  updateCustomWidget: PropTypes.func.isRequired,
  addAsset: PropTypes.func.isRequired,
  showAppNotification: PropTypes.func.isRequired,
  openFinderRecentlyDeleted: PropTypes.func.isRequired,
  // graphql
  fetchGraphqlFiles: PropTypes.func.isRequired,
  graphqlFiles: PropTypes.shape({
    byId: PropTypes.object.isRequired
  }),
  // stylesheets
  availableStylesheets: PropTypes.array.isRequired,
  stylesheets: PropTypes.shape({
    byId: PropTypes.object.isRequired
  }).isRequired,
  fetchStylesheets: PropTypes.func.isRequired,
  // javascripts
  availableJavascripts: PropTypes.array.isRequired,
  javascripts: PropTypes.shape({
    byId: PropTypes.object.isRequired
  }).isRequired,
  fetchJavascripts: PropTypes.func.isRequired
};
export default connect(makeMapStateToProps, mapDispatchToProps)(LinkedFiles);